
import React from 'react';
import './TelerikViewerWithTopScrollbar.css';

interface Props {
  id?: string;
  viewerKey?: string;
  children?: React.ReactNode;
}

interface State {
  syncReady: boolean;
}

class TelerikViewerWithTopScrollbar extends React.Component<Props, State> {
  topScrollbarRef: React.RefObject<HTMLDivElement>;
  topSpacerRef: React.RefObject<HTMLDivElement>;
  viewerHostRef: React.RefObject<HTMLDivElement>;
  pageContainerEl: HTMLDivElement | null;
  resizeObserver: ResizeObserver | null;
  _cleanup: (() => void) | null;

  // rAF + caching to prevent redundant writes and RO warnings
  private pendingWidthRaf: number | null;
  private lastScrollWidth: number;
  private lastClientWidth: number;

  constructor(props: Props) {
    super(props);
    this.topScrollbarRef = React.createRef<HTMLDivElement>();
    this.topSpacerRef = React.createRef<HTMLDivElement>();
    this.viewerHostRef = React.createRef<HTMLDivElement>(); // local wrapper to scope query
    this.pageContainerEl = null;
    this.resizeObserver = null;
    this._cleanup = null;
    this.state = { syncReady: false };

    this.pendingWidthRaf = null;
    this.lastScrollWidth = 0;
    this.lastClientWidth = 0;
  }

  componentDidMount() {
    // Try to resolve the .trv-page-container after the viewer renders
    this.tryInitializeSync();
  }

  componentDidUpdate(prevProps: Props) {
    // If the viewer remounts (key/id change), re-init sync
    if (prevProps.id !== this.props.id || prevProps.viewerKey !== this.props.viewerKey) {
      this.detachSync();
      this.tryInitializeSync();
    }
  }

  componentWillUnmount() {
    this.detachSync();
  }

  tryInitializeSync = () => {
    let attempts = 0;
    const attemptFind = () => {
      // Scope the query inside our wrapper to avoid matching other viewers
      const scope = this.viewerHostRef.current || document;
      const el = scope.querySelector<HTMLDivElement>('.trv-page-container');
      if (el) {
        this.pageContainerEl = el;
        this.initSync();
        this.setState({ syncReady: true });
        return;
      }
      attempts += 1;
      if (attempts <= 15) {
        setTimeout(attemptFind, 200); // retry up to ~3s
      }
    };
    attemptFind();
  };

  // Defer writes to rAF and only write when values change
  setWidths = () => {
    const top = this.topScrollbarRef.current;
    const spacer = this.topSpacerRef.current;
    const container = this.pageContainerEl;
    if (!top || !spacer || !container) return;

    // READS (safe during RO notification)
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;

    // If nothing changed, skip scheduling
    if (scrollWidth === this.lastScrollWidth && clientWidth === this.lastClientWidth) return;

    // Schedule DOM writes in next frame (avoid RO loop warning)
    if (this.pendingWidthRaf !== null) return;
    this.pendingWidthRaf = requestAnimationFrame(() => {
      this.pendingWidthRaf = null;

      // Re-check after layout settles
      const sw = container.scrollWidth;
      const cw = container.clientWidth;

      // Only write if values differ from our last committed ones
      if (sw !== this.lastScrollWidth) {
        spacer.style.width = `${sw}px`;        // top spacer = full horizontal content width
        this.lastScrollWidth = sw;
      }

      if (cw !== this.lastClientWidth) {
        // IMPORTANT: set the visible width of the top bar to match the viewer's visible width
        top.style.width = `${cw}px`;           // top bar width = container client width (matches bottom)
        this.lastClientWidth = cw;
      }
    });
  };

  initSync = () => {
    const top = this.topScrollbarRef.current;
    const spacer = this.topSpacerRef.current;
    const container = this.pageContainerEl;
    if (!top || !spacer || !container) return;

    this.setWidths();

    // Sync scrollLeft both ways with guard to avoid feedback loops
    const onTopScroll = () => {
      if (container.scrollLeft !== top.scrollLeft) {
        container.scrollLeft = top.scrollLeft;
      }
    };
    const onContainerScroll = () => {
      if (top.scrollLeft !== container.scrollLeft) {
        top.scrollLeft = container.scrollLeft;
      }
    };

    top.addEventListener('scroll', onTopScroll, { passive: true });
    container.addEventListener('scroll', onContainerScroll, { passive: true });

    // Window resize — container clientWidth may change
    const onWindowResize = () => this.setWidths();
    window.addEventListener('resize', onWindowResize);

    // Observe the container for content width changes (e.g., different report/page sizes, zoom)
    this.resizeObserver = new ResizeObserver(() => this.setWidths());
    this.resizeObserver.observe(container);

    // Store cleanup so we can detach later
    this._cleanup = () => {
      top.removeEventListener('scroll', onTopScroll);
      container.removeEventListener('scroll', onContainerScroll);
      window.removeEventListener('resize', onWindowResize);
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      // Cancel any pending rAF to avoid late writes
      if (this.pendingWidthRaf !== null) {
        cancelAnimationFrame(this.pendingWidthRaf);
        this.pendingWidthRaf = null;
      }
      // Reset last values so next init will re-apply widths
      this.lastScrollWidth = 0;
      this.lastClientWidth = 0;
    };
  };

  detachSync = () => {
    if (this._cleanup) {
      this._cleanup();
      this._cleanup = null;
    }
    this.pageContainerEl = null;
    this.setState({ syncReady: false });
  };

  render() {
    const { children } = this.props;

    return (
      <div className="trv-viewer-wrapper" ref={this.viewerHostRef}>
        {/* Top horizontal scrollbar */}
        <div
          className="trv-top-scrollbar"
          ref={this.topScrollbarRef}
          aria-label="Report horizontal scrollbar"
          tabIndex={0}
        >
          <div className="trv-top-scrollbar-spacer" ref={this.topSpacerRef} />
        </div>

        {/* Your Telerik Report Viewer goes here */}
        {children}

        {/* Accessibility helper (optional) */}
        <div aria-live="polite" style={{ position: 'absolute', left: -9999 }}>
          {this.state.syncReady ? 'Scrollbar synchronized' : 'Initializing scrollbar'}
        </div>
      </div>
    );
  }
}

export default TelerikViewerWithTopScrollbar;